import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, ArrowUpRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatUSD2 } from "../../utils/format";
import { toast } from "sonner";

/* Fix CS (2026-09-14): "al hacer click sobre X se deberían visualizar…" (Heber,
   14/09). Cada tarjeta del dashboard se abre acá y muestra de dónde sale su número:
   los montos, los porcentajes, el gráfico y la lista completa. Es solo lectura. */
const opts = { withCredentials: true };
const AZUL = "#2E6DA4";

const TIPOS = {
  ingresos: { url: "ingresos", titulo: "Ingresos — todas las operaciones de cobro" },
  gastos: { url: "gastos", titulo: "Gastos — todo lo registrado en el ERP" },
  proyectos: { url: "proyectos", titulo: "Proyectos activos — estado financiero y avance" },
  personal: { url: "personal", titulo: "Personal — desglose desde RRHH" },
};

function Tarjeta({ label, valor, sub, color = "text-gray-900", testId }) {
  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2.5" data-testid={testId}>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-bold ${color} tabular-nums`}>{valor}</p>
      {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
    </div>
  );
}

function Barras({ datos, claveX = "nombre", claveY = "monto", testId }) {
  if (!datos?.length) return <p className="text-xs text-gray-400 py-6 text-center">Sin datos para graficar.</p>;
  return (
    <div style={{ width: "100%", height: 210 }} data-testid={testId}>
      <ResponsiveContainer>
        <BarChart data={datos} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis dataKey={claveX} tick={{ fontSize: 10, fill: "#9CA3AF" }} interval={0} angle={-20} textAnchor="end" height={54} />
          <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} tickFormatter={v => formatPYGCompact(v)} width={62} />
          <Tooltip formatter={v => formatPYG(v)} contentStyle={{ fontSize: 12, borderRadius: 10 }} />
          <Bar dataKey={claveY} radius={[6, 6, 0, 0]}>
            {datos.map((_, i) => <Cell key={i} fill={AZUL} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Tabla({ columnas, filas, testId, vacio = "Sin registros." }) {
  const [q, setQ] = useState("");
  const texto = q.trim().toLowerCase();
  const visibles = !texto ? filas : filas.filter(f => columnas.some(c => String(f[c.k] ?? "").toLowerCase().includes(texto)));
  return (
    <div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrar…"
        className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs w-56 mb-2" data-testid={`${testId}-buscar`} />
      <div className="overflow-auto max-h-72 rounded-xl border border-gray-100">
        <table className="w-full text-xs" data-testid={testId}>
          <thead className="sticky top-0 bg-gray-50 [&_th]:sticky [&_th]:top-0 [&_th]:bg-gray-50">
            <tr className="text-left text-[10px] uppercase text-gray-400">
              {columnas.map(c => <th key={c.k} className={`px-3 py-2 font-bold ${c.num ? "text-right" : ""}`}>{c.t}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {visibles.length === 0 ? (
              <tr><td colSpan={columnas.length} className="px-3 py-8 text-center text-gray-400">{vacio}</td></tr>
            ) : visibles.map((f, i) => (
              <tr key={i} className="hover:bg-gray-50/60">
                {columnas.map(c => (
                  <td key={c.k} className={`px-3 py-2 ${c.num ? "text-right tabular-nums" : ""} ${c.fuerte ? "font-semibold text-gray-900" : "text-gray-600"}`}>
                    {c.fmt ? c.fmt(f[c.k], f) : (f[c.k] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-1.5">{visibles.length} de {filas.length}</p>
    </div>
  );
}

export default function DetalleDashboardModal({ tipo, onClose }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [alcance, setAlcance] = useState("todos");
  const navigate = useNavigate();
  const meta = TIPOS[tipo];

  useEffect(() => {
    if (!meta) return;
    setCargando(true);
    axios.get(`${API}/dashboard/${meta.url}`, opts)
      .then(({ data }) => setData(data))
      .catch(err => { toast.error(err.response?.data?.detail || "No se pudo cargar el detalle"); onClose(); })
      .finally(() => setCargando(false));
  }, [meta, onClose]);

  if (!meta) return null;
  const gs = v => formatPYG(v || 0);
  const usd = v => formatUSD2(v);   // Fix FG (2026-10-08)

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`detalle-${tipo}`}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <p className="font-bold text-gray-900">{meta.titulo}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="detalle-cerrar"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-5">
          {cargando || !data ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : tipo === "ingresos" ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Tarjeta label="Facturado" valor={gs(data.total_facturado)} testId="ing-facturado" />
                <Tarjeta label="Cobrado" valor={gs(data.total_cobrado)} sub={`${data.pct_cobrado}% de lo facturado`} color="text-emerald-600" testId="ing-cobrado" />
                <Tarjeta label="Pendiente" valor={gs(data.pendiente)} color="text-amber-600" testId="ing-pendiente" />
                <Tarjeta label="Operaciones" valor={String(data.operaciones.length)} testId="ing-cantidad" />
              </div>
              {data.usd?.facturado > 0 && (
                /* Fix FG (2026-10-08): "se debe poder ver el ingreso en USD para comprender de dónde
                   sale el monto convertido a PYG" (CEN). */
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-800 flex flex-wrap gap-x-6 gap-y-1" data-testid="ing-usd">
                  <span>En USD: facturado <strong>{usd(data.usd.facturado)}</strong></span>
                  <span>cobrado <strong>{usd(data.usd.cobrado)}</strong></span>
                  <span>pendiente <strong>{usd(data.usd.pendiente)}</strong></span>
                  <span>Lo cobrado se suma en Gs. al cambio de cada cobro; lo pendiente, al de hoy: 1 USD = {Math.round(data.tc_hoy || 0).toLocaleString("es-PY")} Gs ({data.tc_hoy_fuente})</span>
                </div>
              )}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Cobrado por mes</p>
                <Barras datos={data.serie} claveX="mes" testId="ing-serie" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Cobrado por cliente</p>
                <Barras datos={data.por_cliente} testId="ing-por-cliente" />
              </div>
              <Tabla testId="ing-tabla" filas={data.operaciones} columnas={[
                { k: "numero", t: "N° COB", fuerte: true }, { k: "cliente", t: "Cliente" },
                { k: "proyecto", t: "Proyecto" }, { k: "fecha", t: "Fecha" }, { k: "estado", t: "Estado" },
                { k: "monto", t: "Monto", num: true, fmt: gs }, { k: "cobrado", t: "Cobrado", num: true, fmt: gs },
                { k: "pct", t: "%", num: true, fmt: v => `${v}%` },
                ...(data.usd?.facturado > 0 ? [   // Fix FG
                  { k: "monto_usd", t: "Monto USD", num: true, fmt: v => (v ? usd(v) : "—") },
                  { k: "tipo_cambio", t: "Cambio", num: true, fmt: v => (v ? Math.round(v).toLocaleString("es-PY") : "—") },
                ] : []),
              ]} />
            </>
          ) : tipo === "gastos" ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Tarjeta label="Gasto total" valor={gs(data.total)} color="text-red-600" testId="gas-total" />
                <Tarjeta label="Registros" valor={String(data.cantidad)} testId="gas-cantidad" />
                <Tarjeta label="Obras con gasto" valor={String(data.por_obra.length)} testId="gas-obras" />
                <Tarjeta label="Rubros" valor={String(data.por_rubro.length)} testId="gas-rubros" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Gasto por mes</p>
                <Barras datos={data.serie} claveX="mes" testId="gas-serie" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Por obra</p>
                  <Barras datos={data.por_obra.slice(0, 8)} testId="gas-por-obra" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Por rubro</p>
                  <Barras datos={data.por_rubro.slice(0, 8)} testId="gas-por-rubro" />
                </div>
              </div>
              <Tabla testId="gas-tabla" filas={data.gastos} columnas={[
                { k: "fecha", t: "Fecha" }, { k: "obra", t: "Obra", fuerte: true }, { k: "rubro", t: "Rubro" },
                { k: "origen", t: "Origen" }, { k: "descripcion", t: "Descripción" },
                { k: "solicitante", t: "Solicitante" }, { k: "monto", t: "Monto", num: true, fmt: gs },
              ]} />
            </>
          ) : tipo === "proyectos" ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Tarjeta label="Proyectos" valor={String(data.totales.cantidad)} testId="pro-cantidad" />
                <Tarjeta label="Presupuesto" valor={gs(data.totales.presupuesto)} testId="pro-presupuesto" />
                <Tarjeta label="Gastado" valor={gs(data.totales.gastado)} color="text-red-600" testId="pro-gastado" />
                <Tarjeta label="Cobrado" valor={gs(data.totales.cobrado)} color="text-emerald-600" testId="pro-cobrado" />
                <Tarjeta label="Avance físico" valor={`${data.totales.avance_promedio}%`} testId="pro-avance" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500 uppercase">Alcance:</span>
                {[["todos", `Todos (${data.proyectos.length})`],
                  ["largo", `Largo alcance (${data.largo_alcance.length})`],
                  ["corto", `Corto alcance (${data.corto_alcance.length})`]].map(([v, l]) => (
                  <button key={v} onClick={() => setAlcance(v)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${alcance === v ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    data-testid={`pro-alcance-${v}`}>{l}</button>
                ))}
                <span className="text-[11px] text-gray-400 ml-1">Largo alcance = plazo de {data.dias_umbral} días o más</span>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Avance físico por obra</p>
                <Barras claveY="avance_fisico" claveX="codigo" testId="pro-grafico"
                  datos={(alcance === "todos" ? data.proyectos : alcance === "largo" ? data.largo_alcance : data.corto_alcance).slice(0, 10)} />
              </div>
              <Tabla testId="pro-tabla" vacio="No hay obras en esta clasificación."
                filas={alcance === "todos" ? data.proyectos : alcance === "largo" ? data.largo_alcance : data.corto_alcance}
                columnas={[
                  { k: "codigo", t: "Código", fuerte: true }, { k: "nombre", t: "Obra" }, { k: "cliente", t: "Cliente" },
                  { k: "alcance", t: "Alcance" }, { k: "avance_fisico", t: "Avance", num: true, fmt: v => `${v}%` },
                  { k: "presupuesto", t: "Presupuesto", num: true, fmt: gs },
                  { k: "gastado", t: "Gastado", num: true, fmt: (v, f) => `${gs(v)} (${f.pct_gastado}%)` },
                  { k: "cobrado", t: "Cobrado", num: true, fmt: (v, f) => `${gs(v)} (${f.pct_cobrado}%)` },
                  { k: "obra_id", t: "", fmt: (v) => (
                      <button onClick={() => { onClose(); navigate(`/obra/${v}`); }}
                        className="flex items-center gap-1 text-[#2E6DA4] font-semibold hover:underline" data-testid={`pro-ver-${v}`}>
                        Ver <ArrowUpRight size={11} />
                      </button>) },
                ]} />
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Tarjeta label="Total de la empresa" valor={String(data.total_empresa)} testId="per-total" />
                <Tarjeta label="Activos" valor={String(data.activos)} testId="per-activos" />
                <Tarjeta label="Funcionarios" valor={String(data.funcionarios)} testId="per-funcionarios" />
                <Tarjeta label="Electricistas" valor={String(data.electricistas)} color="text-[#2E6DA4]" testId="per-electricistas" />
                <Tarjeta label="Asignados a obra" valor={String(data.en_obra)} testId="per-en-obra" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Personal por obra</p>
                <Barras datos={data.por_obra.map(o => ({ nombre: o.obra, monto: o.cantidad }))} testId="per-grafico" />
              </div>
              <div className="space-y-2">
                {data.por_obra.map(o => (
                  <details key={o.obra} className="rounded-xl border border-gray-100 p-3" data-testid={`per-obra-${o.obra}`}>
                    <summary className="cursor-pointer text-sm font-semibold text-gray-800">
                      {o.obra} <span className="text-gray-400 font-normal">· {o.cantidad} persona(s)</span>
                    </summary>
                    <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {o.personal.map((p, i) => (
                        <li key={i} className="text-xs text-gray-600 flex justify-between border-b border-gray-50 py-1">
                          <span>{p.nombre}</span><span className="text-gray-400">{p.cargo || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>, document.body
  );
}
